/**
 * Critérios adicionados pelo perito (E38.F3). // ux-neutrality:allow doc-comment
 *
 * Abaixo dos 10 critérios canônicos, o perito registra critérios próprios // ux-neutrality:allow doc-comment
 * (nome + LR assinado + observação) quando analisa algo fora da régua fixa. // ux-neutrality:allow doc-comment
 * Cada um exibe o mesmo selo de status (derivado do LR pelo backend) dos // ux-neutrality:allow doc-comment
 * canônicos e sai no laudo em paralelo a eles, sem alterar o consolidado. // ux-neutrality:allow doc-comment
 */
import { useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import type { CustomCriterionCreateData, CustomCriterionData } from '@/lib/custom_criteria';
import { LR_MAX, LR_MIN } from '@/lib/lr_adjustment';

export interface CustomCriteriaSectionProps {
  criteria: CustomCriterionData[];
  busy: boolean;
  error: boolean;
  onAdd: (input: CustomCriterionCreateData) => Promise<boolean>;
  onRemove: (id: string) => Promise<void>;
}

function CustomCriterionRow({
  criterion,
  onRemove,
}: {
  criterion: CustomCriterionData;
  onRemove: (id: string) => Promise<void>;
}): JSX.Element {
  return (
    <li
      className={`criteria-row criteria-row--${criterion.status}`}
      data-testid={`custom-criterion-${criterion.id}`}
    >
      <div className="criteria-header">
        <span className="criteria-header-main">
          <span className="criteria-name">{criterion.name}</span>
          <span className={`criteria-badge criteria-badge--${criterion.status}`}>
            {ptBR.criterio.status[criterion.status]}
          </span>
        </span>
        <span className="criteria-lr">LR = {criterion.lr.toPrecision(3)}</span>
        <button
          type="button"
          className="criteria-chevron-btn"
          aria-label={ptBR.workspaceCaso.customCriteriaRemove}
          onClick={() => void onRemove(criterion.id)}
          data-testid={`custom-criterion-remove-${criterion.id}`}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {criterion.observation && <p className="criteria-evidence">{criterion.observation}</p>}
    </li>
  );
}

function isValidLr(value: string): boolean {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= LR_MIN && parsed <= LR_MAX;
}

function CustomCriterionForm({
  busy,
  error,
  onAdd,
  onCancel,
}: {
  busy: boolean;
  error: boolean;
  onAdd: (input: CustomCriterionCreateData) => Promise<boolean>;
  onCancel: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [lr, setLr] = useState('');
  const [observation, setObservation] = useState('');
  const canSave = name.trim().length > 0 && isValidLr(lr) && !busy;

  async function handleSave(): Promise<void> {
    const ok = await onAdd({
      name: name.trim(),
      lr: Number(lr),
      observation: observation.trim() ? observation.trim() : null,
    });
    if (ok) onCancel();
  }

  return (
    <form
      className="custom-criteria-form"
      data-testid="custom-criteria-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) void handleSave();
      }}
    >
      <label className="custom-criteria-field">
        {ptBR.workspaceCaso.customCriteriaName}
        <input
          type="text"
          value={name}
          maxLength={120}
          placeholder={ptBR.workspaceCaso.customCriteriaNamePlaceholder}
          onChange={(e) => setName(e.target.value)}
          data-testid="custom-criteria-name"
        />
      </label>
      <label className="custom-criteria-field">
        {ptBR.workspaceCaso.customCriteriaLr}
        <input
          type="number"
          step="any"
          min={LR_MIN}
          max={LR_MAX}
          value={lr}
          onChange={(e) => setLr(e.target.value)}
          data-testid="custom-criteria-lr"
        />
      </label>
      <label className="custom-criteria-field">
        {ptBR.workspaceCaso.customCriteriaObservation}
        <textarea
          value={observation}
          maxLength={2000}
          rows={2}
          placeholder={ptBR.workspaceCaso.customCriteriaObservationPlaceholder}
          onChange={(e) => setObservation(e.target.value)}
          data-testid="custom-criteria-observation"
        />
      </label>
      {error && (
        <p
          role="alert"
          className="text-caption text-danger-700"
          data-testid="custom-criteria-error"
        >
          {ptBR.workspaceCaso.customCriteriaError}
        </p>
      )}
      <div className="custom-criteria-actions">
        <button type="submit" disabled={!canSave} data-testid="custom-criteria-save">
          {ptBR.workspaceCaso.customCriteriaSave}
        </button>
        <button type="button" onClick={onCancel} data-testid="custom-criteria-cancel">
          {ptBR.workspaceCaso.customCriteriaCancel}
        </button>
      </div>
    </form>
  );
}

export function CustomCriteriaSection({
  criteria,
  busy,
  error,
  onAdd,
  onRemove,
}: CustomCriteriaSectionProps): JSX.Element {
  const [adding, setAdding] = useState(false);
  return (
    <section className="custom-criteria" aria-label={ptBR.workspaceCaso.customCriteriaSectionTitle}>
      {criteria.length > 0 && (
        <ul className="custom-criteria-list" data-testid="custom-criteria-list">
          {criteria.map((c) => (
            <CustomCriterionRow key={c.id} criterion={c} onRemove={onRemove} />
          ))}
        </ul>
      )}
      {adding ? (
        <CustomCriterionForm
          busy={busy}
          error={error}
          onAdd={onAdd}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button
          type="button"
          className="custom-criteria-add"
          onClick={() => setAdding(true)}
          data-testid="custom-criteria-add"
        >
          + {ptBR.workspaceCaso.customCriteriaAdd}
        </button>
      )}
      <p className="custom-criteria-hint text-caption text-fg-muted">
        {ptBR.workspaceCaso.customCriteriaHint}
      </p>
    </section>
  );
}
